import React, { useState, useEffect, useRef } from 'react';
import { 
  Heart, Users, MapPin, ArrowRight, MessageCircle, Mail,
  Instagram, ChevronRight, Menu, X, Gift, Building, ExternalLink, ChevronDown, Sparkles, HandHeart, Globe, Star, Zap, Award, TrendingUp
} from 'lucide-react';
import '../VivaCharity.css';
import '../BrazilMap.css';
import BrazilMap from './BrazilMap';

// Hook para animação de scroll
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Animar contadores quando a seção de ações estiver visível
          if (entry.target.id === 'acoes') {
            setTimeout(() => {
              const counters = document.querySelectorAll('.charity-stat-number[data-count]');
              counters.forEach(counter => {
                const target = parseInt(counter.getAttribute('data-count'));
                const duration = 2000;
                const increment = target / (duration / 16);
                let current = 0;
                const updateCounter = () => {
                  current += increment;
                  if (current < target) {
                    counter.textContent = Math.floor(current) + '+';
                    requestAnimationFrame(updateCounter);
                  } else {
                    counter.textContent = target + '+';
                  }
                };
                updateCounter();
              });
            }, 300);
          }
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Floating Hearts Animation Component
const FloatingHearts = () => {
  return (
    <div className="floating-hearts">
      {[...Array(6)].map((_, i) => (
        <div 
          key={i} 
          className="floating-heart"
          style={{
            '--delay': `${i * 0.5}s`,
            '--x': `${Math.random() * 100}%`,
            '--duration': `${4 + Math.random() * 3}s`
          }}
        >
          <Heart size={20 + Math.random() * 15} />
        </div>
      ))}
    </div>
  );
};

// Animated Background Shapes
const AnimatedShapes = () => {
  return (
    <div className="animated-shapes">
      <div className="shape shape-1"></div>
      <div className="shape shape-2"></div>
      <div className="shape shape-3"></div>
      <div className="shape shape-4"></div>
      <div className="shape shape-5"></div>
    </div>
  );
};

const VivaCharity = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Refs para animações
  const [sobreRef, sobreVisible] = useScrollAnimation();
  const [acoesRef, acoesVisible] = useScrollAnimation();
  const [impactoRef, impactoVisible] = useScrollAnimation();
  const [contatoRef, contatoVisible] = useScrollAnimation();
  
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="charity-site">
      {/* Animated Background */}
      <AnimatedShapes />
      
      {/* NAV */}
      <nav className={`charity-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="charity-nav-inner">
          <a href="#inicio" className="charity-nav-brand" onClick={(e) => { e.preventDefault(); scrollTo('inicio'); }}>
            <HandHeart size={28} className="brand-icon animated-icon" />
            <span>VIVA IMPACT</span>
          </a>
          
          <div className="charity-nav-menu">
            <a href="#inicio" onClick={(e) => { e.preventDefault(); scrollTo('inicio'); }}>Início</a>
            <a href="#sobre" onClick={(e) => { e.preventDefault(); scrollTo('sobre'); }}>Quem Somos</a>
            <a href="#acoes" onClick={(e) => { e.preventDefault(); scrollTo('acoes'); }}>Nossas Ações</a>
            <a href="#impacto" onClick={(e) => { e.preventDefault(); scrollTo('impacto'); }}>Nosso Impacto</a>
            <a href="#contato" onClick={(e) => { e.preventDefault(); scrollTo('contato'); }}>Contato</a>
          </div>

          <a href="#contato" className="charity-nav-btn pulse-btn" onClick={(e) => { e.preventDefault(); scrollTo('contato'); }}>
            <Heart size={18} className="btn-heart" />
            Ajude Também
          </a>

          <button className="charity-nav-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="charity-mobile-nav">
            <a href="#inicio" onClick={(e) => { e.preventDefault(); scrollTo('inicio'); }}>Início</a>
            <a href="#sobre" onClick={(e) => { e.preventDefault(); scrollTo('sobre'); }}>Quem Somos</a>
            <a href="#acoes" onClick={(e) => { e.preventDefault(); scrollTo('acoes'); }}>Nossas Ações</a>
            <a href="#impacto" onClick={(e) => { e.preventDefault(); scrollTo('impacto'); }}>Nosso Impacto</a>
            <a href="#contato" onClick={(e) => { e.preventDefault(); scrollTo('contato'); }}>Contato</a>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section id="inicio" className="charity-hero">
        <FloatingHearts />
        
        <div className="charity-hero-bg">
          <div className="charity-hero-shape shape-1" style={{ transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)` }}></div>
          <div className="charity-hero-shape shape-2" style={{ transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)` }}></div>
          <div className="charity-hero-shape shape-3" style={{ transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)` }}></div>
        </div>
        
        <div className="charity-hero-content">
          <div className="charity-hero-text">
            <div className="charity-hero-badge bounce-in">
              <Sparkles size={16} className="sparkle-icon" />
              <span>Responsabilidade Social</span>
            </div>
            
            <h1 className="hero-title-animated">
              <span className="title-line">Juntos, podemos</span>
              <span className="title-line gradient-text-animated">transformar vidas</span>
            </h1>
            
            <p className="fade-in-up">
              A Viva Impact atua com amor e propósito. Realizamos ações sociais 
              em todo o Brasil, levando esperança, alegria e dignidade para 
              comunidades que mais precisam.
            </p>
            
            <div className="charity-hero-actions">
              <a href="/acoes-sociais" className="charity-btn-primary shimmer-btn">
                <Heart size={20} className="btn-icon" />
                <span>Ver Nossas Ações</span>
                <div className="btn-shimmer"></div>
              </a>
              <a href="#sobre" className="charity-btn-secondary hover-lift" onClick={(e) => { e.preventDefault(); scrollTo('sobre'); }}>
                Conhecer Nossa História
                <ArrowRight size={18} className="arrow-icon" />
              </a>
            </div>
            
            <div className="charity-hero-stats glass-card">
              <div className="charity-hero-stat count-up">
                <span className="stat-number">5.000+</span>
                <span className="stat-label">Pessoas Ajudadas</span>
              </div>
              <div className="stat-divider pulse-divider"></div>
              <div className="charity-hero-stat count-up">
                <span className="stat-number">15+</span>
                <span className="stat-label">Cidades</span>
              </div>
              <div className="stat-divider pulse-divider"></div>
              <div className="charity-hero-stat count-up">
                <span className="stat-number">1.000+</span>
                <span className="stat-label">Cestas Doadas</span>
              </div>
            </div>
          </div>
          
          <div className="charity-hero-image parallax-container">
            <div className="charity-hero-image-wrapper hover-3d" style={{
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: 'transparent',
              backgroundColor: 'transparent',
              boxShadow: 'none'
            }}>
              <img 
                src="/images/vimpact-logo-nobg.png" 
                alt="VIMPACT Agency - Transformando Vidas"
                style={{
                  objectFit: 'contain', 
                  width: '90%', 
                  height: 'auto',
                  maxWidth: '600px',
                  maxHeight: '600px',
                  background: 'none',
                  backgroundColor: 'transparent',
                  mixBlendMode: 'screen',
                  filter: 'brightness(1.1) contrast(1.15) saturate(1.2)',
                  imageRendering: '-webkit-optimize-contrast',
                  WebkitFontSmoothing: 'antialiased',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)',
                  willChange: 'transform'
                }}
              />
              <div className="charity-hero-image-badge pop-in">
                <Heart size={24} className="badge-heart pulse-icon" />
                <span>+5.000 vidas impactadas</span>
              </div>
            </div>
            
            {/* Floating badges */}
            <div className="floating-badge badge-1">
              <Gift size={20} />
              <span>Cestas</span>
            </div>
            <div className="floating-badge badge-2">
              <Users size={20} />
              <span>Famílias</span>
            </div>
            <div className="floating-badge badge-3">
              <Star size={20} />
              <span>Amor</span>
            </div>
          </div>
        </div>
        
        <div className="charity-scroll-indicator" onClick={() => scrollTo('sobre')} aria-label="Role para descobrir">
          <span className="scroll-indicator-label">Role para descobrir</span>
          <div className="scroll-mouse" aria-hidden="true">
            <div className="scroll-mouse-wheel" />
          </div>
          <div className="scroll-indicator-arrows" aria-hidden="true">
            <span /><span /><span />
          </div>
        </div>
      </section>

      {/* SOBRE */}
      <section id="sobre" className="charity-sobre" ref={sobreRef}>
        <div className={`charity-sobre-content ${sobreVisible ? 'animate-in' : ''}`}>
          <div className="charity-sobre-text">
            <div className="charity-section-badge slide-in-left">
              <Users size={16} />
              <span>Quem Somos</span>
            </div>
            
            <h2 className="title-reveal">Uma agência com <span className="highlight-text">coração</span></h2>
            
            <p className="charity-sobre-lead fade-in">
              Não somos apenas uma empresa. Somos um movimento que acredita 
              no poder da solidariedade e na transformação social.
            </p>
            
            <p className="fade-in">
              A Viva Impact nasceu com o propósito de fazer a diferença. Trabalhamos 
              em parceria com comunidades, instituições e voluntários para levar ajuda 
              real onde ela é mais necessária. Transparência, compromisso e resultados 
              comprovados são nossos pilares.
            </p>
            
            <div className="charity-sobre-values">
              <div className="charity-value hover-scale">
                <div className="value-icon blue ripple-effect">
                  <Heart size={24} />
                </div>
                <div>
                  <h4>Amor ao Próximo</h4>
                  <p>Cada ação é feita com carinho e dedicação</p>
                </div>
              </div>
              
              <div className="charity-value hover-scale">
                <div className="value-icon pink ripple-effect">
                  <Users size={24} />
                </div>
                <div>
                  <h4>Comunidade</h4>
                  <p>Presença ativa nas regiões atendidas</p>
                </div>
              </div>
              
              <div className="charity-value hover-scale">
                <div className="value-icon green ripple-effect">
                  <Star size={24} />
                </div>
                <div>
                  <h4>Transparência</h4>
                  <p>Resultados documentados e comprovados</p>
                </div>
              </div>
            </div>
            
            <a href="https://instagram.com/vimpactdigital" target="_blank" rel="noopener noreferrer" className="charity-instagram-link magnetic-btn">
              <Instagram size={20} /> 
              Acompanhe no Instagram
              <ArrowRight size={16} className="arrow-slide" />
            </a>
          </div>
          
          <div className="charity-sobre-images">
            <div className="charity-image-mosaic stagger-animation">
              <div className="mosaic-main-photo">
                <img 
                  src="https://res.cloudinary.com/dnrowstdh/image/upload/v1774061579/vivaimpact_migrated/cyky6t4vjz1feewhh11b.jpg" 
                  alt="Voluntários em ação"
                />
                <div className="image-shine"></div>
                <div className="mosaic-badge">
                  <span>Nossas Ações</span>
                </div>
              </div>
              <div className="mosaic-small-photos">
                <div className="mosaic-photo">
                  <img 
                    src="https://res.cloudinary.com/dnrowstdh/image/upload/v1774061577/vivaimpact_migrated/vsfntmqrivzse3hca3uk.jpg" 
                    alt="Distribuição de alimentos"
                  />
                  <div className="image-shine"></div>
                </div>
                <div className="mosaic-photo">
                  <img 
                    src="https://res.cloudinary.com/dnrowstdh/image/upload/v1774061561/vivaimpact_migrated/esx0pcvvb5slofdc3dlr.jpg" 
                    alt="Comunidade unida"
                  />
                  <div className="image-shine"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAPA + IMPACTO COMBINADOS */}
      <section id="impacto" className="charity-mapa-impacto" ref={impactoRef}>
        <div className={`charity-mapa-impacto-content ${impactoVisible ? 'animate-in' : ''}`}>
          
          {/* Header */}
          <div className="charity-impacto-text">
            <div className="charity-section-badge green pop-in">
              <Globe size={16} className="spin-slow" />
              <span>Nosso Impacto</span>
            </div>
            
            <h2 className="title-reveal">
              Números que representam <span className="gradient-text-animated">esperança</span>
            </h2>
            
            <p className="fade-in">
              Cada número representa uma história de transformação. 
              São famílias que receberam alimentos, crianças que ganharam sorrisos 
              e comunidades que encontraram esperança.
            </p>
          </div>

          {/* Layout: Mapa + Cards lado a lado */}
          <div className="mapa-impacto-layout">
            {/* Mapa */}
            <div className="mapa-impacto-map">
              <BrazilMap showInHomepage={true} />
              <div className="charity-mapa-footer">
                <a href="/mapa-acoes" className="charity-btn-mapa shimmer-btn">
                  <div className="btn-content">
                    <MapPin size={20} />
                    <span>Ver Mapa Completo</span>
                    <ArrowRight size={20} />
                  </div>
                  <div className="btn-shimmer"></div>
                </a>
              </div>
            </div>

            {/* Cards de Impacto */}
            <div className="mapa-impacto-cards">
              <div className="charity-impacto-card hover-glow card-1">
                <div className="impacto-icon-wrapper">
                  <TrendingUp size={32} />
                </div>
                <div className="impacto-number counter-animate">600+</div>
                <div className="impacto-label">Famílias beneficiadas</div>
                <div className="impacto-desc">Cestas básicas e suprimentos entregues</div>
                <div className="card-decoration"></div>
              </div>
              
              <div className="charity-impacto-card hover-glow card-2">
                <div className="impacto-icon-wrapper">
                  <MapPin size={32} />
                </div>
                <div className="impacto-number counter-animate">15+</div>
                <div className="impacto-label">Cidades alcançadas</div>
                <div className="impacto-desc">Em diversas regiões do Brasil</div>
                <div className="card-decoration"></div>
              </div>
              
              <div className="charity-impacto-card hover-glow card-3">
                <div className="impacto-icon-wrapper">
                  <Gift size={32} />
                </div>
                <div className="impacto-number counter-animate">1.000+</div>
                <div className="impacto-label">Cestas básicas</div>
                <div className="impacto-desc">Alimentos para quem precisa</div>
                <div className="card-decoration"></div>
              </div>
              
              <div className="charity-impacto-card hover-glow card-4">
                <div className="impacto-icon-wrapper">
                  <Zap size={32} />
                </div>
                <div className="impacto-number counter-animate">2.000+</div>
                <div className="impacto-label">Brinquedos doados</div>
                <div className="impacto-desc">Alegria para as crianças</div>
                <div className="card-decoration"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AÇÕES SOCIAIS - SIMPLIFICADO */}
      <section id="acoes" className="charity-acoes" ref={acoesRef}>
        <div className={`charity-acoes-content ${acoesVisible ? 'animate-in' : ''}`}>
          <div className="charity-section-header">
            <div className="charity-section-badge pink pop-in">
              <Gift size={16} className="rotate-icon" />
              <span>Nossas Ações</span>
            </div>
            
            <h2 className="title-wave">
              Transformando <span className="gradient-text-animated">vidas</span> por todo Brasil
            </h2>
            
            <p className="fade-in-up">
              Cada ação é planejada com amor e executada com dedicação. 
              Clique no botão abaixo para conhecer todas as nossas iniciativas 
              que já impactaram milhares de pessoas.
            </p>
          </div>

          {/* Estatísticas com mais animação */}
          <div className="charity-stats-row">
            <div className="charity-stat-card blue hover-float">
              <div className="stat-card-glow"></div>
              <div className="charity-stat-icon bounce-icon">
                <Heart size={28} />
              </div>
              <div className="charity-stat-number" data-count="600">0+</div>
              <div className="charity-stat-label">Famílias Impactadas</div>
              <div className="stat-decoration"></div>
            </div>
            
            <div className="charity-stat-card pink hover-float">
              <div className="stat-card-glow"></div>
              <div className="charity-stat-icon bounce-icon">
                <MapPin size={28} />
              </div>
              <div className="charity-stat-number" data-count="15">0+</div>
              <div className="charity-stat-label">Cidades Alcançadas</div>
              <div className="stat-decoration"></div>
            </div>
            
            <div className="charity-stat-card green hover-float">
              <div className="stat-card-glow"></div>
              <div className="charity-stat-icon bounce-icon">
                <Gift size={28} />
              </div>
              <div className="charity-stat-number" data-count="1000">0+</div>
              <div className="charity-stat-label">Doações Realizadas</div>
              <div className="stat-decoration"></div>
            </div>
          </div>

          {/* Preview Visual das Ações */}
          <div className="acoes-preview-section">
            <div className="preview-images-container">
              <div className="preview-image-stack">
                <img src="https://res.cloudinary.com/dnrowstdh/image/upload/v1774061579/vivaimpact_migrated/cyky6t4vjz1feewhh11b.jpg" alt="Ação 1" className="preview-img img-1" />
                <img src="https://res.cloudinary.com/dnrowstdh/image/upload/v1774061577/vivaimpact_migrated/vsfntmqrivzse3hca3uk.jpg" alt="Ação 2" className="preview-img img-2" />
                <img src="https://res.cloudinary.com/dnrowstdh/image/upload/v1774061561/vivaimpact_migrated/esx0pcvvb5slofdc3dlr.jpg" alt="Ação 3" className="preview-img img-3" />
              </div>
              
              <div className="preview-info-card glass-card">
                <div className="preview-info-icon">
                  <Award size={32} />
                </div>
                <h3>+15 Ações Realizadas</h3>
                <p>Dia das Crianças, Cestas Básicas, Uniformes Esportivos, Ajuda Humanitária e muito mais!</p>
                <div className="preview-tags">
                  <span className="preview-tag">São Paulo</span>
                  <span className="preview-tag">Bahia</span>
                  <span className="preview-tag">Paraná</span>
                  <span className="preview-tag">+10</span>
                </div>
              </div>
            </div>
          </div>

          {/* Botão CTA Grande */}
          <div className="charity-actions-cta">
            <a href="/acoes-sociais" className="charity-btn-mega shimmer-btn">
              <div className="btn-content">
                <Heart size={24} className="btn-heart-icon" />
                <div className="btn-text">
                  <span className="btn-main-text">Ver Todas as Ações</span>
                  <span className="btn-sub-text">Conheça cada projeto em detalhes</span>
                </div>
                <ArrowRight size={24} className="btn-arrow" />
              </div>
              <div className="btn-shimmer"></div>
              <div className="btn-glow"></div>
            </a>
          </div>
        </div>
      </section>

      {/* CONTATO */}
      <section id="contato" className="charity-contato" ref={contatoRef}>
        <div className={`charity-contato-content ${contatoVisible ? 'animate-in' : ''}`}>
          <div className="charity-contato-text">
            <div className="charity-section-badge blue pop-in">
              <MessageCircle size={16} />
              <span>Contato</span>
            </div>
            
            <h2 className="title-reveal">
              Quer fazer parte dessa <span className="gradient-text-animated">transformação</span>?
            </h2>
            
            <p className="fade-in">
              Entre em contato conosco! Seja para saber mais sobre nossas ações, 
              propor parcerias ou solicitar ajuda para sua comunidade.
            </p>
            
            <div className="charity-contato-info">
              <a href="mailto:contato@vivaimpact.com" className="charity-contato-item hover-lift">
                <Mail size={24} className="contact-icon" />
                <div>
                  <strong>E-mail</strong>
                  <span>contato@vivaimpact.com</span>
                </div>
              </a>
              
              <a href="https://instagram.com/vimpactdigital" target="_blank" rel="noopener noreferrer" className="charity-contato-item hover-lift">
                <Instagram size={24} className="contact-icon" />
                <div>
                  <strong>Instagram</strong>
                  <span>@vimpactdigital</span>
                </div>
              </a>
            </div>
          </div>
          
          <div className="charity-contato-cards">
            <a href="https://whatsapp.com/channel/0029Vb7b9kTJUM2W4WsrXg3J" target="_blank" rel="noopener noreferrer" className="charity-contato-card hover-float-card">
              <div className="contato-card-icon green">
                <MessageCircle size={28} />
              </div>
              <h4>WhatsApp</h4>
              <p>Siga nosso canal oficial</p>
              <ExternalLink size={16} className="card-arrow" />
              <div className="card-shine"></div>
            </a>
            
            <a href="/solicitar-doacoes" className="charity-contato-card hover-float-card">
              <div className="contato-card-icon pink">
                <Heart size={28} />
              </div>
              <h4>Solicitar Ajuda</h4>
              <p>Preencha nosso formulário</p>
              <ChevronRight size={16} className="card-arrow" />
              <div className="card-shine"></div>
            </a>
            
            <a href="mailto:parcerias@vivaimpact.com" className="charity-contato-card hover-float-card">
              <div className="contato-card-icon blue">
                <Building size={28} />
              </div>
              <h4>Parcerias</h4>
              <p>Empresas e instituições</p>
              <ExternalLink size={16} className="card-arrow" />
              <div className="card-shine"></div>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="charity-footer">
        <div className="charity-footer-top">
          <div className="charity-footer-brand">
            <div className="charity-footer-logo">
              <HandHeart size={32} className="footer-icon pulse-icon" />
              <span>VIVA IMPACT</span>
            </div>
            <div className="charity-footer-social">
              <a href="https://instagram.com/vimpactdigital" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="social-btn hover-scale">
                <Instagram size={24} />
              </a>
            </div>
          </div>
          
          <div className="charity-footer-links">
            <div className="charity-footer-col">
              <h4>Navegação</h4>
              <a href="#inicio" onClick={(e) => { e.preventDefault(); scrollTo('inicio'); }}>Início</a>
              <a href="#sobre" onClick={(e) => { e.preventDefault(); scrollTo('sobre'); }}>Quem Somos</a>
              <a href="#acoes" onClick={(e) => { e.preventDefault(); scrollTo('acoes'); }}>Nossas Ações</a>
              <a href="#contato" onClick={(e) => { e.preventDefault(); scrollTo('contato'); }}>Contato</a>
            </div>
            
            <div className="charity-footer-col">
              <h4>Empresa</h4>
              <p><strong>VIVA IMPACT MARKETING DIGITAL LTDA.</strong></p>
              <p>CNPJ: 63.222.122/0001-42</p>
            </div>
            
            <div className="charity-footer-col">
              <h4>Contato</h4>
              <a href="mailto:contato@vivaimpact.com">contato@vivaimpact.com</a>
            </div>
          </div>
        </div>
        
        <div className="charity-footer-bottom">
          <span>© 2026 VIVA IMPACT MARKETING DIGITAL LTDA. Todos os direitos reservados.</span>
          <div className="charity-legal-links">
            <a href="/privacidade">Privacidade</a>
            <span>•</span>
            <a href="/termos">Termos</a>
            <span>•</span>
            <a href="/lgpd">LGPD</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VivaCharity;
